import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextField, TextFieldSpecial } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { Pressable } from 'components/Pressable';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useConfirm } from 'hooks/useConfirm';
import { useSelector } from 'hooks/useSelector';
import { formatCurrency } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { toNumber } from 'lodash';
import React from 'react';
import { View } from 'react-native';
import {
  selectRemoteFieldGroups,
  selectRemoteFieldSectionIdsByOrganisation,
  selectRemoteFieldSections,
} from 'store/remote/selectors';
import { UID } from 'types/types';

type Costs = {
  cost: number | null;
  minCost: number | null;
  maxCost: number | null;
  fieldGroups: Record<
    string,
    { newCost: number | null; existingCost: number | null }
  >;
};

type Props = {
  id: UID;
  name: string;
  defaultCost: number;
  defaultMinCost: number;
  defaultMaxCost: number | null;
  costs: Costs | null | undefined;
  onChange: (id: UID, cost: Costs) => void;
  onDelete: (id: UID) => void;
  isDefault: boolean;
  onClose: () => void;
  initIsExpanded?: boolean;
};

const defaultNewCosts: Costs = {
  cost: null,
  minCost: null,
  maxCost: null,
  fieldGroups: {},
};

export const TemplatePricingItem: React.FC<Props> = React.memo(
  ({
    id,
    name,
    defaultCost,
    defaultMinCost,
    defaultMaxCost,
    costs,
    onChange,
    onDelete,
    onClose,
    isDefault,
    initIsExpanded,
  }) => {
    const [isCollapsed, setIsCollapsed] =
      React.useState<boolean>(!initIsExpanded);

    const confirm = useConfirm();
    const [costState, setCostState] = React.useState(
      !costs ? defaultNewCosts : costs,
    );

    const sectionIds =
      useSelector(selectRemoteFieldSectionIdsByOrganisation)?.[id] || [];
    const fieldSections = useSelector(selectRemoteFieldSections)!;
    const fieldGroups = useSelector(selectRemoteFieldGroups)!;

    const calculatedFieldGroups: {
      id: number;
      name: string;
      newCost: number | null;
      existingCost: number | null;
    }[] = [];

    sectionIds.forEach((sectionId) => {
      const section = fieldSections[sectionId];
      section?.groupIds.forEach((groupId) => {
        const group = fieldGroups[groupId];
        if (group?.repeatable) {
          calculatedFieldGroups.push({
            id: group.id,
            name: group.name,
            newCost: group.newCost,
            existingCost: group.existingCost,
          });
        }
      });
    });

    const handleChange = (
      type: 'cost' | 'minCost' | 'maxCost',
      value: string,
    ) => {
      const newCosts = {
        ...(costState || defaultNewCosts),
        [type]: value !== '' ? toNumber(value) : null,
      };
      setCostState(newCosts);
      onChange(id, newCosts);
    };

    const handleChangeFieldGroup = (
      type: 'newCost' | 'existingCost',
      fieldGroupId: number,
      value: string,
    ) => {
      const preNewCosts = costState ? { ...costState } : { ...defaultNewCosts };
      const newCosts = {
        ...preNewCosts,
        fieldGroups: {
          ...preNewCosts.fieldGroups,
          [fieldGroupId]: {
            ...(preNewCosts.fieldGroups[fieldGroupId] || {
              newCost: null,
              existingCost: null,
            }),
            [type]: value !== '' ? toNumber(value) : null,
          },
        },
      };
      setCostState(newCosts);
      onChange(id, newCosts);
    };

    const handleDelete = () => {
      confirm({
        title: 'Delete Customisation',
        subtitle: name,
        text: 'The template and element pricing will be reset back to default for this organisation.',
        confirmText: 'Delete',
        destructive: true,
        onConfirm: () => onDelete(id),
      });
    };

    return (
      <View>
        <Pressable
          onPress={() => setIsCollapsed(!isCollapsed)}
          style={{
            paddingLeft: theme.padding,
            paddingRight: theme.padding + theme.paddingHorizontalHalf,
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: isCollapsed ? 0 : theme.paddingHorizontalHalf,
            gap: theme.padding,
          }}
        >
          <IconButton
            icon={ICONS.CaretDownSolid}
            onPress={() => setIsCollapsed(!isCollapsed)}
            style={
              isCollapsed ? { transform: [{ rotate: '-90deg' }] } : undefined
            }
          />
          <Pressable
            style={{ flex: 1 }}
            onPress={() => setIsCollapsed(!isCollapsed)}
          >
            <Txt weight="medium" size="small">
              {name}
            </Txt>
          </Pressable>
          {!isDefault && (
            <Button text="Delete" onPress={handleDelete} destructive={true} />
          )}
        </Pressable>
        {!isCollapsed && (
          <Card>
            <ViewWithSeparators
              style={{
                backgroundColor: theme.colors.white,
              }}
            >
              <View
                style={{
                  backgroundColor: '#fafafa',
                  paddingVertical: theme.padding / 2,
                  flexDirection: 'row',
                }}
              >
                <Txt
                  size="xsmall"
                  weight="medium"
                  style={{ flex: 1, paddingLeft: theme.padding }}
                >
                  Template
                </Txt>
                <Txt
                  size="xsmall"
                  style={{
                    width: 81,
                    color: theme.colors.fieldValueText,
                    textAlign: 'right',
                    paddingHorizontal: theme.padding,
                  }}
                >
                  Base
                </Txt>
                <Txt
                  size="xsmall"
                  style={{
                    width: 81,
                    color: theme.colors.fieldValueText,
                    textAlign: 'right',
                    paddingHorizontal: theme.padding,
                  }}
                >
                  Min
                </Txt>
                <Txt
                  size="xsmall"
                  style={{
                    width: 81,
                    color: theme.colors.fieldValueText,
                    textAlign: 'right',
                    paddingHorizontal: theme.padding,
                  }}
                >
                  Max
                </Txt>
              </View>
              {/** Template */}
              <Card withShadow={false} marginHorizontal={false}>
                <ViewWithSeparators horizontal>
                  <TextField
                    value={
                      costState.cost || costState.cost === 0
                        ? `${costState.cost}`
                        : ''
                    }
                    textLeft="Cost"
                    textLeftStyle={{ color: theme.colors.black }}
                    containerStyle={{ flex: 1 }}
                    inputStyle={{ textAlign: 'right' }}
                    onChange={(value) => handleChange('cost', value)}
                    placeholder={formatCurrency(defaultCost, false)}
                    onEsc={onClose}
                    autoFocusWeb={isDefault}
                    autoFocusDelay
                    special={TextFieldSpecial.currency}
                  />
                  <TextField
                    value={
                      costState.minCost || costState.minCost === 0
                        ? `${costState.minCost}`
                        : ''
                    }
                    containerStyle={{ width: 80 }}
                    inputStyle={{ textAlign: 'right' }}
                    textLeftStyle={{ color: theme.colors.black }}
                    onChange={(value) => handleChange('minCost', value)}
                    placeholder={
                      defaultMinCost !== null
                        ? formatCurrency(defaultMinCost, false)
                        : 'No Min'
                    }
                    onEsc={onClose}
                    special={TextFieldSpecial.currency}
                  />
                  <TextField
                    value={
                      costState.maxCost || costState.maxCost === 0
                        ? `${costState.maxCost}`
                        : ''
                    }
                    containerStyle={{ width: 80 }}
                    inputStyle={{ textAlign: 'right' }}
                    textLeftStyle={{ color: theme.colors.black }}
                    onChange={(value) => handleChange('maxCost', value)}
                    placeholder={
                      defaultMaxCost !== null
                        ? formatCurrency(defaultMaxCost, false)
                        : 'No Max'
                    }
                    onEsc={onClose}
                    special={TextFieldSpecial.currency}
                  />
                </ViewWithSeparators>
              </Card>
              {/** Group */}
              {!!calculatedFieldGroups.length && (
                <View
                  style={{
                    backgroundColor: '#fafafa',
                    paddingVertical: theme.padding / 2,
                    flexDirection: 'row',
                  }}
                >
                  <Txt
                    size="xsmall"
                    weight="medium"
                    style={{ flex: 1, paddingLeft: theme.padding }}
                  >
                    Elements
                  </Txt>
                  <Txt
                    size="xsmall"
                    style={{
                      width: 81,
                      color: theme.colors.fieldValueText,
                      textAlign: 'right',
                      paddingHorizontal: theme.padding,
                    }}
                  >
                    New
                  </Txt>
                  <Txt
                    size="xsmall"
                    style={{
                      width: 81,
                      color: theme.colors.fieldValueText,
                      textAlign: 'right',
                      paddingHorizontal: theme.padding,
                    }}
                  >
                    Existing
                  </Txt>
                </View>
              )}
              {calculatedFieldGroups.map((group) => {
                const groupCost = costState.fieldGroups[group.id];
                return (
                  <Card
                    key={group.id}
                    withShadow={false}
                    marginHorizontal={false}
                  >
                    <ViewWithSeparators horizontal>
                      <CardRow
                        label={group.name}
                        labelNumberOfLines={2}
                        style={{ flex: 1 }}
                        contentStyle={{ paddingTop: 0, paddingBottom: 0 }}
                      />
                      <TextField
                        value={
                          groupCost?.newCost || groupCost?.newCost === 0
                            ? `${groupCost.newCost}`
                            : ''
                        }
                        containerStyle={{ width: 80 }}
                        inputStyle={{ textAlign: 'right' }}
                        onChange={(value) =>
                          handleChangeFieldGroup('newCost', group.id, value)
                        }
                        placeholder={formatCurrency(group.newCost || 0, false)}
                        onEsc={onClose}
                        special={TextFieldSpecial.currency}
                      />
                      <TextField
                        value={
                          groupCost?.existingCost ||
                          groupCost?.existingCost === 0
                            ? `${groupCost.existingCost}`
                            : ''
                        }
                        containerStyle={{ width: 80 }}
                        inputStyle={{ textAlign: 'right' }}
                        onChange={(value) =>
                          handleChangeFieldGroup(
                            'existingCost',
                            group.id,
                            value,
                          )
                        }
                        placeholder={formatCurrency(
                          group.existingCost || 0,
                          false,
                        )}
                        onEsc={onClose}
                        special={TextFieldSpecial.currency}
                      />
                    </ViewWithSeparators>
                  </Card>
                );
              })}
            </ViewWithSeparators>
          </Card>
        )}
      </View>
    );
  },
);
